/**
 * OrderOrderContainer
 * --
 */
import React from 'react';
import OrderOrderPresenter from './OrderOrderPresenter';
import {
  useDefaultShopDataStack,
  useUserAction,
  useUserDataAction,
  useUserDataStack,
  useUserStack,
} from 'store';
import MessageAlert, {
  DOMAIN_URL,
  HOST_URL,
  jsonToQueryString,
  PAYMENT_STORE_ID,
  DEPLOY_TYPE,
  PAYMENT_PC_URL,
  openPopup,
  calculateTax,
  getPaymentDueDate,
  distributePointByRatio,
  calculateRentalMonthlyPayment,
  PAYMENT_URL,
  isMobile,
  POINT_STORE_ID,
  extractApplicationCommission,
} from 'utils';
import Formatter from 'utils/Formatter';
import { useDeleteDeliveryAddress } from 'hooks/DeliveryAddressHooks';
import { useCreateShopOrder } from 'hooks/ShopOrderHooks';
import {
  usePaymentCancelCard,
  usePaymentPayBilling,
  usePaymentDeRegistBilling,
} from 'hooks/PaymentHooks.';
import { useBulkDeleteCart } from 'hooks/CartHooks';
import { useMyPoint } from 'hooks/custom';
import { useDeleteCard } from 'hooks/CardHooks';
import dayjs from 'dayjs';
import { useCreateTempPayment } from 'hooks/TempHooks';
import Logger from 'utils/logger';
import calculateApplyPolicy from 'utils/productPolicyUtils';
import { JmPayCharge } from './components';

const logger = new Logger('OrderOrder');

const DIRECT_DEPOSIT_PAYMENT_INFO = {
  authno: '05',
  isscd: '102-910073-84504',
  bankName: '하나은행',
  accountNumber: '102-910073-84504',
};

const OrderOrderContainer = (props) => {
  /* ===== ROUTE ===== */
  /* ===== PROPS ===== */
  const { navigate, location, searchParams, screenSize } = props;

  const deliveryType = searchParams.get('type') ?? 'delivery';
  const paymentType = searchParams.get('payment') ?? null;
  const payData = searchParams.get('pay') ?? null;
  // const orData = searchParams.get('order') ?? null;
  /* ===== Mutate ===== */
  // 배송지 삭제
  const { deleteDeliveryAddress } = useDeleteDeliveryAddress();
  // 오더 Temp 생성
  const { createTempPayment } = useCreateTempPayment();
  // 오더 생성
  const { createShopOrder } = useCreateShopOrder();
  // 카드 취소
  const { cancelCard } = usePaymentCancelCard();
  // 장바구니 삭제
  const { bulkDeleteCart } = useBulkDeleteCart();
  // 빌링 결제
  const { payBilling } = usePaymentPayBilling();
  // 빌링 카드 삭제
  const { deregistBilling } = usePaymentDeRegistBilling();
  //카드 삭제
  const { deleteCard } = useDeleteCard();

  /* ===== Store ===== */
  const today = React.useMemo(() => dayjs().unix(), []);
  const userDataAction = useUserDataAction();
  const userInfo = useUserStack();
  const userAction = useUserAction();
  const {
    orderList,
    shopOrderList,
    cartList,
    deliveryAddress,
    couponList,
    cardList,
  } = useUserDataStack();
  const { settings } = useDefaultShopDataStack();

  /* ===== Data ===== */
  // NOTE: 사용 가능 포인트
  const PointData = useMyPoint(userInfo.user_id);

  /* ===== STATE ===== */
  // 주문 상품 목록
  const [shopOrder, setShopOrder] = React.useState([]);
  // 결제 정보
  const [paymentInfo, setPaymentInfo] = React.useState({});
  // 배송지
  const [selectedDelivery, setSelectedDelivery] = React.useState(null);
  // 로딩
  const [isLoading, setIsLoading] = React.useState(false);
  // 결제 수단
  const [paymentMethod, setPaymentMethod] = React.useState('CARD');
  // 결제 리턴값
  const [paymentReturn, setPaymentReturn] = React.useState(null);
  // 선택 쿠폰
  const [selectedCoupon, setSelectedCoupon] = React.useState(null);
  // 쿠폰 적용할 상품
  const [selectedProduct, setSelectedProduct] = React.useState(null);
  // 결제 카드 선택
  const [selectedCard, setSelectedCard] = React.useState(null);
  // 배송 시작일
  const [deliveryDate, setDeliveryDate] = React.useState(null);
  // 사용 포인트
  const [usePoint, setUsePoint] = React.useState(0);
  // 동의 여부
  const [isChecked, setIsChecked] = React.useState(false);
  // 결제 진행중 여부
  const [isProgress, setIsProgress] = React.useState(false);
  // 서비스 동의여부
  const [isAgree, setIsAgree] = React.useState(false);
  // 환불불가 상품 포함 여부
  const [isRefundNotAllowed, setIsRefundNotAllowed] = React.useState(false);
  const [isRefundAgree, setIsRefundAgree] = React.useState(false);
  // 제이엠페이 추가 결제
  const [isJmPayCharge, setIsJmPayCharge] = React.useState(0);
  const [isOpenModal, setIsOpenModal] = React.useState(false);
  const [tidId, setTidId] = React.useState(null);

  /* ===== VARIABLE ===== */
  const getPolicyDiscountPrice = (policy, cartNum) =>
    policy?.policy_type === 'COUPON'
      ? (policy?.discount_price ?? 0)
      : (policy?.discount_price ?? 0) * cartNum;

  /* ===== FUNCTION ===== */
  /**
   * ANCHOR 결제 유효성 검사
   * --
   * @returns {boolean}
   */
  const handlePaymentValidateCheck = () => {
    // 배송지 유효성 검사
    if (deliveryType !== 'store_pickup') {
      if (!selectedDelivery) {
        MessageAlert.info('배송지를 선택해주세요!');
        return false;
      }

      const {
        recipient_address,
        recipient_postcode,
        recipient_phone,
        recipient_address_detail,
      } = selectedDelivery;

      if (
        !recipient_postcode ||
        !recipient_address ||
        !recipient_phone ||
        !recipient_address_detail
      ) {
        MessageAlert.info('배송지를 선택해주세요!');
        return false;
      }
    }

    // 정기배송/렌탈일 경우
    if (deliveryType === 'subscription' || deliveryType === 'rental') {
      if (!deliveryDate) {
        MessageAlert.info(
          deliveryType === 'subscription'
            ? '배송시작일 선택해주세요!'
            : '배송일을 선택해주세요!',
        );
        return false;
      }

      if (!selectedCard) {
        MessageAlert.info('결제 카드를 선택해주세요!');
        return false;
      }
    }

    // 이용약관 동의 여부
    if (!isChecked) {
      MessageAlert.info('주문정보 및 서비스 이용약관에 동의해주세요!');
      return false;
    }

    // 픽업/렌탈 서비스 동의 여부
    if (
      (deliveryType === 'store_pickup' || deliveryType === 'rental') &&
      !isAgree
    ) {
      MessageAlert.info(
        deliveryType === 'store_pickup'
          ? '픽업 서비스에 동의해주세요.'
          : '렌탈 서비스에 동의해주세요.',
      );
      return false;
    }

    // 환불 불가상품 동의 여부
    if (isRefundNotAllowed && !isRefundAgree) {
      MessageAlert.info('환불불가상품 내용에 동의해주세요.');
      return false;
    }

    const paymentPrice =
      deliveryType !== 'rental'
        ? paymentInfo.total_final_price
        : calculateRentalMonthlyPayment(
            paymentInfo.total_final_price,
            shopOrder?.[0]?.product_information?.[0]?.rental_option,
          ) +
          paymentInfo?.advance_price +
          paymentInfo?.additional_cost;

    if (paymentPrice - usePoint < 1000) {
      MessageAlert.info('최소 주문금액은 1,000원 이상입니다.');
      return false;
    }

    // JM페이 유효성 검사
    if (paymentMethod === 'JM_PAY') {
      if (paymentPrice - usePoint > userInfo?.jm_pay) {
        setIsJmPayCharge(paymentPrice - usePoint - userInfo?.jm_pay);
        return false;
      }
    }

    return true;
  };

  /**
   * ANCHOR 오더 데이터 정제
   * --
   * @param {string} dtype - [DELIVERY, STORE_PICKUP SUBSCRIPTION, RENTAL]
   * @param {string} ptype - [CARD, JM_PAY, NAVER_PAY, KAKAO_PAY]
   * @returns {object}
   */
  // logger.debug('shopOrder =======>', shopOrder);
  // logger.debug('paymentInfo =======>', paymentInfo);
  const getOrderData = (dtype = 'DELIVERY', ptype = 'CARD', pay = null) => {
    const mobileCheck = isMobile();
    const isDirectDeposit = ptype === 'DIRECT_DEPOSIT';

    // 출고지별 금액 배열
    let finalArr = [];
    const newShopOrder = shopOrder.map((order) => {
      const {
        delivery_price,
        remote_delivery_price,
        total_origin_price,

        product_information,

        final_total_discount_price,
        final_total_reserve_price,
      } = order;

      // 총 상품 원가 - 총 할인 금액 (배송비 제외)
      const finalPrice = total_origin_price - final_total_discount_price;

      finalArr.push(finalPrice);

      return {
        ...order,
        final_discount: -final_total_discount_price,
        final_reward_point:
          usePoint && usePoint > 0 ? 0 : final_total_reserve_price,
        final_price:
          dtype !== 'RENTAL'
            ? finalPrice
            : product_information?.[0]?.product_final_price +
              paymentInfo?.additional_cost +
              paymentInfo?.advance_price,
        payment_method: ptype,
        payment_state: isDirectDeposit ? 'PENDING' : 'COMPLETED',
        order_route: mobileCheck ? 'APP' : 'PC',
        payment_info: pay,
        delivery_address:
          dtype !== 'STORE_PICKUP'
            ? selectedDelivery
            : order?.forwarding_location,
        final_delivery_price: delivery_price + remote_delivery_price,
        cash_bill_info: null, // TODO 현금영수증
        is_cashbill: 'N', // TODO 현금영수증 발행 여부
        type: dtype,
        product_information: product_information.map((product) => {
          const {
            cart_num,
            num,
            apply_commission,
            // 불필요 데이터
            application_status,
            commission,
            apply_grade_benefit,
            apply_policy,
            cart_type,
            created_at,
            discount_rate,
            discount_type,
            forwarding_location,
            grade_benefit_info,
            isWish,
            is_bundle_delivery,
            is_present,
            is_store_pickup,
            max_regular_delivery_count,
            remote_area_postcode,
            rental_default_option,
            reserve_rate,
            reserve_type,
            updated_at,
            // !불필요 데이터
            ...rest
          } = product;

          return {
            ...rest,
            commission: apply_commission, // 적용 수수료
            num: cart_num, // 구매 수량
            payment_price:
              rest?.origin_total_price * cart_num - rest?.total_discount_price, // 상품별 결제금액
            discount_price: -(rest?.total_discount_price ?? 0), // 상품별 할인금
            reserve_price:
              usePoint && usePoint > 0 ? 0 : (rest?.total_reserve_price ?? 0), // 상품별 적립금
            deposit_status: isDirectDeposit ? 'PENDING' : 'COMPLETED',
            delivery_date:
              dtype === 'SUBSCRIPTION' || dtype === 'RENTAL'
                ? dayjs(dayjs(deliveryDate, '2025/07/23')).startOf('day').unix()
                : null,
            regular_updated_at:
              dtype === 'SUBSCRIPTION'
                ? getPaymentDueDate(
                    dayjs(deliveryDate, '2025/07/23'),
                    rest?.regular_cycle,
                    2,
                  )
                : null,
            rental_price:
              dtype === 'RENTAL' ? product?.product_final_price : null,
            card_id:
              !isDirectDeposit && selectedCard ? selectedCard.card_id : null,
          };
        }),
      };
    });

    // 출고지별 포인트 비율
    const amountRatio = distributePointByRatio(finalArr, usePoint);

    // 오더 데이터 정제
    const orderData = {
      user_id: userInfo.user_id,
      payment_method: ptype,
      payment_state: isDirectDeposit ? 'PENDING' : 'COMPLETED',
      ...paymentInfo,
      total_use_jm_pay:
        ptype === 'JM_PAY' ? -(paymentInfo.total_final_price - usePoint) : 0,
      shop_order: newShopOrder.map((order, index) => ({
        ...order,
        final_price:
          order.final_price + order.final_delivery_price - amountRatio[index], // 출고지별 결제 금액
        use_point: -amountRatio[index], // 사용 포인트
        use_jm_pay:
          ptype === 'JM_PAY'
            ? -(
                order.final_price +
                order.final_delivery_price -
                amountRatio[index]
              )
            : 0, // 사용 JM 페이
      })),
    };

    return orderData;
  };

  /**
   * 결제 팝업
   * --
   * @param {string} method
   * @param {string} type
   * @returns
   */
  const handlePayment = async (method, type) => {
    const submitType = method.toLocaleUpperCase();

    if (!handlePaymentValidateCheck()) return;

    const mobileCheck = isMobile();

    /**
     * PG 결제
     * --
     */
    const createPaymentData = (tempIdx = null) => {
      const paymentData = {
        // SECTION: 공통 파라미터
        sndPaymethod: type,
        sndStoreid: PAYMENT_STORE_ID,
        sndOrdernumber: `JM_${dayjs().unix()}`,

        sndGoodname: `${paymentInfo.product_name}${
          paymentInfo.total_product_count > 0
            ? ` 외 ${paymentInfo.total_product_count}개`
            : ''
        }`,
        sndAmount: `${paymentInfo.total_final_price - usePoint}`, // 결제금액
        sndOrdername: userInfo.user_name,

        // sndEmail: '', // 이메일 [선택]
        // sndMobile: '', // 이동전화('-') 사용불가 [선택]
        // sndServicePeriod: '', // 용역제공기간(2012/08/01 ~ 2012/08/31) [선택]
        iframeYn: 'N', // 결제창 호출방식 (Y: Layer / N: popup) [선택]

        // SECTION: 신용카드 파라미터
        sndCurrencytype: 'WON', // 통화 코드
        sndShowcard: 'C', // 표시 카드사
        sndCharSet: 'utf-8', // 가맹점 문자열 체어셋
        sndReply: `${HOST_URL}/payment/kspay/rcv`,
        // 할부/무이자
        sndInstallmenttype: settings?.payment_setting?.installment_setting, // 할부개월수 (세팅하신 값은 KSPAY결재팝업창에서 고객이 스크롤선택)
        sndInteresttypev: settings?.payment_setting?.non_interest_setting, // 무이자 구분

        sndRtApp: 'jmcoffee://', // 앱 URL 스키마 [선택]
        sndStoreCeoName: '양재석', // 카카오페이 상점대표자명
        sndStorePhoneNo: '010-5138-5511', // 카카오페이 연락처
        sndStoreAddress: '서울 강남구 도산대로11길 15 1~4층', // 카카오페이 주소
        sndQpayType: '0', // 간편결제표시구분 [선택]

        // SECTION: 가상계좌, 에스크로 파라미터
        sndEscrow: '', // 에스크로 사용여부 [선택]
        sndVirDenyBank: '', // 해당은행 미표시 [선택]
        sndVirExpDt: '', // 가상계좌마감일자 (YYYYMMDD) [선택]
        sndVirExpTm: '', // 가상계좌마감일시 (HHMMSS) [선택]

        // SECTION: 계좌이체 파라미터
        sndCashReceipt: '1', // 현금영수증 발급 여부 (0: 발급 x, 1: 발급) [선택]

        // SECTION: 휴대폰결제 파라미터
        sndGoodType: '1', // 상품유형 (1: 실물, 2: 디지털)

        tempId: tempIdx,

        ...(mobileCheck
          ? {
              path: location.pathname,
              searchParams: location.search,
            }
          : {}),
      };

      let paymentURL = '';
      if (mobileCheck) {
        sessionStorage.setItem('orderData', JSON.stringify(shopOrder));
        sessionStorage.setItem(
          'deliveryAddress',
          JSON.stringify(selectedDelivery),
        );
        paymentURL = PAYMENT_URL;
      } else {
        paymentURL = PAYMENT_PC_URL;
      }

      const form = document.createElement('form');
      form.method = 'POST';
      form.action = paymentURL;
      form.target = '_self';

      Object.entries(paymentData).forEach(([key, value]) => {
        const input = document.createElement('input');
        input.type = 'hidden'; // 사용자에게는 보이지 않도록 hidden으로 설정
        input.name = key; // input의 name 속성은 객체의 key
        input.value = value; // input의 value 속성은 객체의 value
        form.appendChild(input);
      });

      form.sndReply.value = `${form.sndReply.value}${jsonToQueryString(
        paymentData,
      )}`;

      document.body.appendChild(form);

      if (mobileCheck) {
        form.submit();
      } else {
        window._pay(form);
      }

      setIsProgress(true);
    };

    /**
     * 빌링 결제
     * --
     */
    const paymentBilling = async () => {
      // NOTE: 빌링 결제
      const res = await payBilling({
        mid: POINT_STORE_ID,
        orderNumb: `JM_${Formatter.dateToUnix()}`,
        userName: userInfo.user_name,
        productType: 'REAL',
        productName: `${paymentInfo.product_name}${
          paymentInfo.total_product_count > 0
            ? ` 외 ${paymentInfo.total_product_count}개`
            : ''
        }`,
        totalAmount:
          deliveryType !== 'rental'
            ? `${paymentInfo.total_final_price - usePoint}`
            : `${
                shopOrder?.[0]?.product_information?.[0]?.product_final_price +
                paymentInfo?.additional_cost +
                paymentInfo?.advance_price -
                usePoint
              }`,
        taxFreeAmount: `${paymentInfo.total_tax_free_amount}`, // 면세 금액
        interestType: 'PG',
        billingToken: selectedCard.billing_token,
        installMonth: `0`,
        currencyType: 'KRW',
      });

      return res;
    };

    /**
     * Confirm
     * --
     * @param {string} msg
     * @returns
     */
    const confirm = (msg) => {
      return window.confirm(msg);
    };

    try {
      let res = null;
      switch (submitType) {
        case 'DELIVERY':
        case 'STORE_PICKUP': {
          if (!type) {
            MessageAlert.warning('결제수단을 선택해주세요!');
            return;
          }

          if (!confirm('주문하시겠습니까?')) return;

          const payMethodType = type.toLocaleUpperCase();

          // SECTION: 빌링결제
          if (payMethodType === 'QUICK_CARD') {
            res = await paymentBilling();

            if (res.status !== 200 || res.data.code !== 'A0200') {
              logger.error('QUICK_CARD Error!!', res);
              MessageAlert.warning('결제에 실패하였습니다!');
              return;
            }

            await handlePaymentSubmit('QUICK-SUCCESS', res.data.data);
            return;
          }

          // SECTION: JM PAY 결제
          if (payMethodType === 'JM_PAY') {
            await handlePaymentSubmit('JM_PAY-SUCCESS', null);
            return;
          }
          if (payMethodType === 'DIRECT_DEPOSIT') {
            await handlePaymentSubmit(
              'DIRECT-DEPOSIT-SUCCESS',
              DIRECT_DEPOSIT_PAYMENT_INFO,
            );
            return;
          }

          // SECTION: 카드 결제
          // NOTE: temp 데이터 생성
          const { status, data, message } = await createTempPayment({
            order_info: getOrderData(submitType, paymentMethod),
            type: 'SHOP_ORDER',
          });

          if (status !== 200) {
            logger.error('createTempPayment', { status, data, message });
            MessageAlert.warning(
              '주문하기에 실패하였습니다. 잠시 후 다시 시도해주세요.',
            );
            return;
          }

          createPaymentData(data.temp_id);

          return;
        }
        case 'SUBSCRIPTION': {
          if (!confirm('정기배송 신청하시겠습니까?')) return;

          res = await paymentBilling();

          if (res.status !== 200 || res.data.code !== 'A0200') {
            logger.error('Subscription Billing Error', res);
            MessageAlert.warning('결제에 실패하였습니다!');
            return;
          }

          await handlePaymentSubmit('BILLING-SUCCESS', res.data.data);
          return;
        }
        case 'RENTAL': {
          if (!confirm('렌탈 신청하시겠습니까?')) return;

          res = await paymentBilling();

          if (res.status !== 200 || res.data.code !== 'A0200') {
            logger.error('Rental Billing Error', res);
            MessageAlert.warning('결제에 실패하였습니다!');
            return;
          }

          await handlePaymentSubmit('BILLING-SUCCESS', res.data.data);
          return;
        }
        default:
          return MessageAlert.info('결제를 진행할 수 없습니다!');
      }
    } catch (err) {
      console.error('[OrderOrderContainer][handlePayment] Error!! ', err);
      MessageAlert.error('결제 실패!');
    }
  };

  /**
   * 주문 생성
   * --
   * @param {string} type
   * @param {object} result
   * @param {array} arr
   */
  // const handledRef = React.useRef();

  const handlePaymentSubmit = React.useCallback(
    async (type, result = null) => {
      const submitType = type.toLocaleUpperCase();

      if (
        !['JM_PAY-SUCCESS', 'DIRECT-DEPOSIT-SUCCESS'].includes(type) &&
        !result
      ) {
        return setPaymentReturn(null);
      }

      try {
        setIsLoading(true);

        switch (submitType) {
          case 'SUCCESS': {
            // SECTION: 장바구니 삭제 처리
            if (deliveryType !== 'rental') handleDeleteCart();

            // SECTION: 쿠폰 사용처리
            if (paymentInfo.total_coupon_info.length) handleUseCoupon();

            MessageAlert.success('상품이 성공적으로 주문되었습니다!');

            sessionStorage.removeItem('orderData');
            sessionStorage.removeItem('deliveryAddress');

            setTimeout(
              () =>
                navigate(`/mypage/order?type=${deliveryType}`, {
                  replace: true,
                  state: { forceReload: true },
                }),
              1000,
            );
            return;
          }
          case 'BILLING-SUCCESS':
          case 'QUICK-SUCCESS':
          case 'JM_PAY-SUCCESS':
          case 'DIRECT-DEPOSIT-SUCCESS':
            const orderData = getOrderData(
              deliveryType.toLocaleUpperCase(),
              submitType === 'JM_PAY-SUCCESS' ? 'JM_PAY' : paymentMethod,
              result,
            );
            // const key = String(
            //   result?.tid ?? result?.tradeKey ?? result?.orderNo ?? '',
            // );
            // if (key && handledRef.current.has(key)) return; // ✅ 중복 차단
            // if (key) handledRef.current.add(key);

            const { status: createOrderStatus, data: createOrderData } =
              await createShopOrder(orderData);
            // SECTION: 결제 실패 처리
            if (createOrderStatus !== 200) {
              const shouldCancelPayment = ![
                'JM_PAY-SUCCESS',
                'DIRECT-DEPOSIT-SUCCESS',
              ].includes(submitType);

              if (shouldCancelPayment) {
                const paymentCancelData = {
                  mid: POINT_STORE_ID,
                  cancelType: 'FULL', // FULL(전체 취소) / PARTIAL(부분 취소)
                  orgTradeKeyType: 'TID', // TID(결제 응답으로 부여받은 tid값) / ORDER_NUMB(결제 요청 시 생성한 가맹점 주문번호)
                  orgTradeKey: result?.tid,
                };

                // NOTE: 결제 취소
                const { data: cancelResponse } =
                  await cancelCard(paymentCancelData);

                if (cancelResponse.code !== 'A0200') {
                  MessageAlert.warning(
                    '결제를 취소 할 수 없습니다.\n관리자에게 문의해주세요.',
                  );
                  return;
                }
              }

              MessageAlert.warning(
                '상품 주문에 실패하였습니다.\n같은 문제가 발생할 시 관리자에게 문의해주세요.',
              );
              return;
            }

            // SECTION: 장바구니 삭제 처리
            if (deliveryType !== 'rental') handleDeleteCart();

            // SECTION: 쿠폰 사용처리
            if (paymentInfo.total_coupon_info.length) handleUseCoupon();

            // SECTION: 오더 데이터 저장 처리
            const newOrderList = [...orderList, ...createOrderData];
            userDataAction.setData('orderList', newOrderList);

            // SECTION: JM 페이 사용처리
            if (submitType === 'JM_PAY-SUCCESS') {
              userAction.setUser({
                ...userInfo,
                jm_pay: result
                  ? Number(result?.jm_pay) -
                    (paymentInfo.total_final_price - usePoint)
                  : userInfo?.jm_pay -
                    (paymentInfo.total_final_price - usePoint),
              });
            }

            MessageAlert.success(
              submitType === 'DIRECT-DEPOSIT-SUCCESS'
                ? '주문이 접수되었습니다. 계좌로 입금해주세요.'
                : '상품이 성공적으로 주문되었습니다!',
            );
            setIsOpenModal(false);
            setTimeout(
              () =>
                navigate(`/mypage/order?type=${deliveryType}`, {
                  replace: true,
                }),
              1000,
            );
            return;

          case 'FAIL':
          default:
            setPaymentReturn(null);
            searchParams.delete('pay');
            MessageAlert.warning('결제에 실패하였습니다!');
            return;
        }
      } catch (err) {
        logger.error(`${submitType} Error!! `, err);
        MessageAlert.error('상품 주문에 실패하였습니다!');

        if (!shopOrder?.length) {
          setTimeout(
            () =>
              navigate(`/order/cart?type=${deliveryType}`, {
                replace: true,
              }),
            500,
          );
        }
      } finally {
        setPaymentReturn(null);
        setTimeout(() => setIsLoading(false), 500);
      }
    },
    // eslint-disable-next-line
    [
      shopOrder,
      paymentInfo,
      paymentMethod,
      deliveryType,
      selectedCard,
      selectedDelivery,
    ],
  );

  /**
   * 구매한 상품 장바구니 삭제
   * --
   */
  const handleDeleteCart = async () => {
    try {
      // 장바구니에서 제거할 아이템 추출
      const removeList = shopOrder.reduce((acc, cur) => {
        const { product_information } = cur;

        product_information.forEach((item) => {
          if (item.is_fixed === 'Y') return;
          else acc.push({ cart_id: item.key });
        });

        return acc;
      }, []);

      if (removeList.length) {
        const { status: removeStatus } = await bulkDeleteCart(removeList);

        if (removeStatus !== 200) {
          MessageAlert.warning(
            '구매한 상품을 장바구니에서 삭제하는데 실패하였습니다.',
          );
        }

        const newCartList = cartList.filter(
          (cart) =>
            !removeList.some((remove) => remove.cart_id === cart.cart_id),
        );

        userDataAction.setData('cartList', newCartList);
      }
    } catch (err) {
      logger.error('Error!!', err);
    }
  };

  /**
   * 쿠폰 사용처리
   * --
   */
  const handleUseCoupon = () => {
    try {
      const { total_coupon_info } = paymentInfo;

      const newCouponList = couponList.map((coupon) => {
        const isExist = total_coupon_info.some(
          (info) =>
            info.coupon_user_id === coupon.coupon_user_id &&
            info.policy_coupon.is_used === 'Y',
        );

        if (isExist) {
          return {
            ...coupon,
            use_date: Formatter.dateToUnix(),
          };
        } else {
          return coupon;
        }
      });

      userDataAction.setData('couponList', newCouponList);
    } catch (err) {
      logger.error('Error!!', err);
    }
  };

  /**
   * 팝업 열기
   * --
   * @param {string} type
   * @param {object} data
   */
  const handleOpenPopup = (type, data) => {
    const query = jsonToQueryString({
      type: type,
      content: JSON.stringify(data),
    });

    if (isMobile()) {
      navigate(`/common/delivery-address${query}`);
    } else {
      openPopup(`/common/delivery-address${query}`);
    }
  };

  /**
   * 배송지 삭제
   * --
   * @param {number} idx
   * @returns
   */
  const handleDeleteAddress = async (idx) => {
    const confirm = window.confirm('배송지를 삭제하시겠습니까?');

    if (!confirm) return;

    try {
      const res = await deleteDeliveryAddress(idx);

      if (res.status !== 200) {
        MessageAlert.warning('배송지를 삭제하는데 실패하였습니다.');
        return;
      }

      userDataAction.removeData('deliveryAddress', 'delivery_address_id', {
        delivery_address_id: idx,
      });
      MessageAlert.success('배송지가 삭제되었습니다.');
    } catch (err) {
      console.error(`[handleDeleteAddress] Error!! : ${err}`);
      MessageAlert.error('배송지를 삭제하는데 실패하였습니다.');
      return;
    }
  };

  /**
   * 배송일 경우 카드 삭제
   * --
   * @param {*} card
   * @returns
   */
  const handleDeleteCard = async (card) => {
    const confirm = (msg = '') => {
      return window.confirm(msg);
    };
    try {
      const check = confirm('카드 정보를 정말 삭제하시겠습니까?');
      if (!check) return;
      // 카드 해지
      const { status: cardStatus } = await deleteCard(card.card_id);
      if (cardStatus !== 200) {
        MessageAlert.warning('카드 삭제에 실패하였습니다!');
        return;
      }
      // 빌링 토큰 삭제
      const { status: billingStatus, data: billingData } =
        await deregistBilling({
          mid: POINT_STORE_ID,
          billingToken: card.billing_token,
        });
      // 빌링 해지 실패 처리
      if (billingStatus !== 200 || billingData.code !== 'A0200') {
        MessageAlert.warning('카드 삭제에 실패하였습니다!');
        return;
      }
      userDataAction.removeData('cardList', 'card_id', {
        card_id: card.card_id,
      });
      MessageAlert.success('카드정보가 성공적으로 삭제되었습니다.');
    } catch (err) {
      console.error('[handleDeleteCard] Error!! ', err);
      MessageAlert.error('카드 삭제에 실패하였습니다!');
    }
  };

  /**
   * 쿠폰 적용
   * --
   */
  const handleApplyCoupon = (callback) => {
    // NOTE: 적용 상품, 적용 쿠폰이 없을 경우 리턴
    if (!selectedProduct) {
      callback && callback();
      return;
    }

    // NOTE: 상품 쿠폰 적용/해제
    const updateProductInformation = (coupon) => {
      // 적용되는 상품의 출고지 찾기
      const findForwarding = shopOrder.find(
        (order) =>
          order.forwarding_location_id ===
          selectedProduct.forwarding_location_id,
      );

      // 출고지를 찾지 못했을 경우 리턴
      if (!findForwarding) {
        callback && callback();
        return;
      }

      // 찾은 출고지에서 cart_id로 쿠폰이 적용되는 상품 찾기
      const fid = findForwarding.product_information.findIndex(
        (product) => product.key === selectedProduct.key,
      );

      // 상품을 찾지 못하였을 경우 리턴
      if (fid === -1) {
        callback?.();
        return;
      }

      // 중복 적용여부에 따른 처리 처리
      const findItem = findForwarding.product_information[fid];

      const totalFindItemPrice =
        (findItem.price + findItem.option_price) * findItem.cart_num;

      const {
        price,
        option_price,
        cart_num,
        apply_grade_benefit,
        apply_policy,
      } = findItem;

      // 할인금 계산
      const { apply_policy_info, final_reserve_price, final_discount_price } =
        calculateApplyPolicy(
          {
            price,
            option_price,
            num: cart_num,
          },
          apply_grade_benefit,
          coupon?.policy_coupon?.is_duplication === 'N' ? null : apply_policy,
          coupon?.policy_coupon,
        );

      const applyCommission = coupon
        ? extractApplicationCommission(
            [
              findItem.commission,
              {
                type: 'COUPON',
                commission_rate: coupon.policy_coupon.commission_rate, // 수수료율
                commission_type: coupon.policy_coupon.commission_type, // 수수료 타입 (R | W)
              },
            ],
            'type',
          )
        : findItem.commission;

      const mapApplyPolicyInfo = new Map(
        apply_policy_info.map((info) => [info.policy_type, info]),
      );
      const gradeDiscountPrice = getPolicyDiscountPrice(
        mapApplyPolicyInfo.get('GRADE'),
        cart_num,
      );
      const promotionDiscountPrice = getPolicyDiscountPrice(
        mapApplyPolicyInfo.get(apply_policy?.policy_type),
        cart_num,
      );
      const couponDiscountPrice = getPolicyDiscountPrice(
        mapApplyPolicyInfo.get('COUPON'),
        cart_num,
      );

      const newProductInfo = {
        ...findItem,
        coupon_info: coupon,
        grade_discount_price: gradeDiscountPrice,
        promotion_discount_price: promotionDiscountPrice,
        coupon_discount_price: couponDiscountPrice,
        apply_policy_info,
        total_reserve_price: final_reserve_price ?? 0,
        total_discount_price: final_discount_price,
        product_final_price:
          findItem.origin_total_price -
          (mapApplyPolicyInfo.get('GRADE')?.discount_price ?? 0) -
          (mapApplyPolicyInfo.get(apply_policy?.policy_type)?.discount_price ??
            0),
        apply_commission: applyCommission,
        ...calculateTax(
          totalFindItemPrice - final_discount_price,
          findItem?.tax_rate,
          findItem?.tax_type,
        ),
      };

      // 상품 정보 업데이트
      const newProductInformation = [
        ...findForwarding.product_information.slice(0, fid),
        newProductInfo,
        ...findForwarding.product_information.slice(fid + 1),
      ];

      // 상품 총 할인금, 적립금 계산
      const finalTotal = newProductInformation.reduce(
        (acc, cur) => {
          acc['final_total_discount_price'] += cur.total_discount_price;
          acc['final_total_reserve_price'] += cur.total_reserve_price;
          acc['final_grade_discount_price'] += cur.grade_discount_price;
          acc['final_coupon_discount_price'] += cur.coupon_discount_price;
          acc['final_promotion_discount_price'] += cur.promotion_discount_price;
          acc['final_supply_price'] += cur.supply_price;
          acc['final_tax_amount'] += cur.tax_amount;
          acc['final_tax_free_amount'] += cur.tax_free_amount;

          return acc;
        },
        {
          final_total_discount_price: 0,
          final_total_reserve_price: 0,
          final_grade_discount_price: 0,
          final_coupon_discount_price: 0,
          final_promotion_discount_price: 0,
          final_supply_price: 0,
          final_tax_amount: 0,
          final_tax_free_amount: 0,
        },
      );

      // 상위 주문 객체 업데이트
      const updatedForwarding = {
        ...findForwarding,
        product_information: newProductInformation,
        coupon_info: newProductInformation.reduce((acc, cur) => {
          if (cur.coupon_info) acc.push(cur.coupon_info);
          return acc;
        }, []), // 같은 출고지내에 사용한 모든 쿠폰 정보
        ...finalTotal,
        delivery_price:
          findForwarding.total_origin_price -
            finalTotal.final_total_discount_price >=
          findForwarding.origin_free_delivery_price
            ? 0
            : findForwarding.origin_delivery_price,
      };

      // shopOrder 상태 업데이트
      setShopOrder((prev) =>
        prev.map((order) =>
          order.forwarding_location_id ===
          updatedForwarding.forwarding_location_id
            ? updatedForwarding
            : order,
        ),
      );
    };

    // NOTE: 선택 쿠폰이 없을 경우 적용된 쿠폰 해제
    if (!selectedCoupon) {
      updateProductInformation(null);
    } else {
      const {
        is_available,
        map_product_policy,
        apply_product,
        discount_price,
        ...rest
      } = selectedCoupon;

      // NOTE: 중복사용 불가능한 쿠폰일 경우
      if (
        selectedCoupon.policy_coupon.is_duplication === 'N' &&
        selectedProduct.apply_policy
      ) {
        const confirm = window.confirm(
          '중복으로 할인이 불가능한 쿠폰입니다.\n쿠폰을 적용하시겠습니까?',
        );

        if (!confirm) return;
      }

      updateProductInformation({ ...rest, discount_price: -discount_price });
    }

    callback?.(selectedProduct, selectedCoupon);
  };

  /**
   * 카드 등록 팝업
   * --
   * @param {string} t
   */
  const handleOpenCardPopup = (t) => {
    if (isMobile()) {
      navigate(`/common/card/register?type=${t}`);
    } else {
      openPopup(`/common/card/register?type=${t}`);
    }
  };

  /**
   * 포인트 분배
   * --
   * @param amounts : object[]
   * @param totalPoints : number
   * @returns
   */
  const handleDistributePointByRatio = (products, totalPoints = 0) => {
    // 총 금액 계산
    const totalAmount = products.reduce(
      (sum, product) => (sum += product.product_final_price),
      0,
    );

    // 남은 포인트 계산
    let remainPoint = totalPoints;
    const newProducts = products.map((product, index) => {
      // 마지막 상품에 남은 포인트를 모두 적용
      const isLast = index === products.length - 1;

      const applyPoint = isLast
        ? remainPoint
        : Math.floor((product.product_final_price / totalAmount) * totalPoints);

      remainPoint -= applyPoint;

      return {
        ...product,
        apply_point: applyPoint,
      };
    });

    return newProducts;
  };

  /* ===== HOOKS ===== */
  /**
   * 주문 목록 데이터 정제
   * --
   */
  const ShopOrderList = React.useMemo(() => {
    // SECTION: 주문 목록 데이터 정제
    const newList = shopOrderList.map((order) => {
      const {
        cart_id,
        brand,
        forwarding_location,
        finalPrice,
        options,
        src,
        policy,
        price,
        option_price,
        // ===== 불필요 데이터 ===== //
        tag,
        average_score,
        review_count,
        forwarding_location_id: forwarding_location_idx,
        is_best,
        is_new,
        is_show,
        is_sold_out,
        product_category_key,
        product_status,
        // ===== 불필요 데이터 ===== //
        ...rest
      } = order;

      // NOTE: 출고지 관련 데이터 추출
      const {
        forwarding_location_id, // 출고지 idx
        remote_area_postcode, // 도서산간지역 배송비
        delivery_price, // 배송비
        free_delivery_price, // 무료배송 기준 금액
      } = forwarding_location;

      // NOTE: 브랜드 관련 데이터 추출
      const {
        shop_information_id, // 매장 idx
        shop_information_name, // 매장명
        organization_id, // 조직 idx
      } = brand;

      return {
        key: cart_id ?? 0,
        forwarding_location_id: forwarding_location_id,
        remote_area_postcode,
        delivery_price,
        free_delivery_price,

        forwarding_location: forwarding_location,

        shop_information_id,
        shop_information_name,
        organization_id,
        thumbnail: src,
        monthly_price:
          rest?.type !== 'RENTAL'
            ? 0
            : calculateRentalMonthlyPayment(rest?.price, rest?.rental_option),
        price,
        option_price,
        origin_total_price: price + option_price,
        apply_commission: rest.commission,
        ...rest,
      };
    });

    // SECTION: 같은 출고지끼리 데이터 그룹화
    const newOrderList = newList.reduce((acc, order) => {
      // 같은 출고지가 존재하는지 체크
      const fid = acc.findIndex(
        (list) => list.forwarding_location_id === order.forwarding_location_id,
      );

      // 최상단 데이터 추출
      const {
        forwarding_location_id,

        forwarding_location,

        remote_area_postcode,
        delivery_price,
        free_delivery_price,
        shop_information_id,
        shop_information_name,
        organization_id,
      } = order;

      // 같은 출고지가 없을 경우 새로운 데이터 push
      if (fid === -1) {
        const newData = {
          forwarding_location_id,

          forwarding_location,

          remote_area_postcode,
          delivery_price,
          free_delivery_price,
          shop_information_id,
          shop_information_name,
          organization_id,
          coupon_info: [],
          product_information: [{ ...order, coupon_info: null }],
        };
        acc.push(newData);
      }
      // 이미 같은 출고지가 있을 경우 상품에 push
      else {
        acc[fid].product_information.push({ ...order, coupon_info: null });
      }

      // 같은 출고지끼리 그룹화한 데이터 반환
      return acc;
    }, []);

    // SECTION: 같은 출고지끼리 상품 총합, 배송비 계산
    return newOrderList.map((order) => {
      const {
        remote_area_postcode,
        delivery_price,
        free_delivery_price,
        product_information,
      } = order;

      // 도서산간지역 배송비 추출
      let remoteFee = 0,
        remoteArea = '';
      if (
        selectedDelivery &&
        remote_area_postcode &&
        remote_area_postcode.length
      ) {
        const { recipient_postcode } = selectedDelivery;

        const findArea = remote_area_postcode.find((area) => {
          const { range } = area;

          return (
            parseInt(recipient_postcode) >= parseInt(range[0]) &&
            parseInt(range[1]) >= parseInt(recipient_postcode)
          );
        });

        if (findArea) {
          remoteFee = findArea.price;
          remoteArea = findArea.city;
        }
      }

      const totalOriginPrice = product_information.reduce(
        (acc, cur) => {
          acc['total_origin_price'] += cur.origin_total_price * cur.cart_num;
          acc['total_monthly_price'] += cur.monthly_price;

          return acc;
        },
        {
          total_origin_price: 0,
          total_monthly_price: 0,
        },
      );

      return {
        ...order,
        origin_delivery_price: delivery_price, // 원본 배송비
        origin_free_delivery_price: free_delivery_price, // 원본 무료 배송 기준 금액
        total_origin_price: totalOriginPrice.total_origin_price, // 상품 원금 총합
        total_monthly_price: totalOriginPrice.total_origin_monthly_price, // 상품 원금 월 결제 금액
        remote_delivery_price: remoteFee, // 도서산간지역 배송비 (아닐 경우 0)
        remote_delivery_area: remoteArea, // 도서산간지역 지역 (아닐 경우 '')
      };
    });
  }, [shopOrderList, selectedDelivery]);

  /**
   * 배송지 정보 정제
   * --
   */
  const DeliveryAddress = React.useMemo(() => {
    let defaultAddress = null;

    if (userInfo.postcode) {
      defaultAddress = {
        delivery_address_id: null,
        recipient_postcode: userInfo?.postcode,
        recipient_address: userInfo?.user_address,
        recipient_address_detail: userInfo?.user_address_detail,
        recipient_name: userInfo?.user_name,
        recipient_phone: userInfo?.user_phone,
        request: '',
      };
    }

    return [defaultAddress && defaultAddress, ...deliveryAddress];
  }, [deliveryAddress, userInfo]);

  /**
   * 쿠폰 데이터 정제
   * --
   */
  const MyCouponData = React.useMemo(() => {
    if (!couponList.length) return [];

    // 사용 가능한 쿠폰 필터
    const useableCoupon = couponList.filter(
      (coupon) => !coupon.use_date && coupon.expired_date > today,
    );

    return useableCoupon;
  }, [couponList, today]);

  /**
   * 카드 데이터 정제
   * --
   */
  const MyCardList = React.useMemo(() => {
    if (!cardList.length) return [];

    const upperType = deliveryType.toLocaleUpperCase();
    const filterType = upperType === 'DELIVERY' ? 'QUICK_CARD' : upperType;

    const newCardList = cardList.filter((card) => card.type === filterType);

    return newCardList;
  }, [deliveryType, cardList]);

  /**
   * 정기 배송/렌탈 최소 배송 시작 가능일
   * --
   */
  const MinDeliveryDate = React.useMemo(() => {
    if (
      !shopOrderList.length &&
      deliveryType !== 'subscription' &&
      deliveryType !== 'rental'
    )
      return 0;

    // NOTE: 주문하려는 상품 중 배송일이 가장 긴 상품에 따라 최소 배송 시작 가능일 추출
    const maxDeliveryDate = shopOrderList.reduce((acc, cur) => {
      if (acc < cur.forwarding_date) {
        acc = cur.forwarding_date;
      }

      return acc;
    }, 0);

    return maxDeliveryDate;
  }, [shopOrderList, deliveryType]);

  /**
   * 사용가능 포인트
   * --
   */
  const MyPoint = React.useMemo(() => {
    const { useable } = PointData;

    return Math.floor(useable);
  }, [PointData]);

  /**
   * 주문목록이 없을 경우
   * --
   */
  React.useEffect(() => {
    if (!shopOrderList.length && !payData) {
      navigate('/order/cart?type=delivery', { replace: true });
    }
  }, [shopOrderList, navigate, payData]);

  /**
   * 페이지 새로고침 경고
   * --
   */
  React.useEffect(() => {
    // 새로고침, 창 닫기 감지
    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = ''; // 크롬에서는 빈 문자열 필수
    };

    // 이벤트 리스너 등록
    window.addEventListener('beforeunload', handleBeforeUnload);

    // 컴포넌트 언마운트 시 리스너 해제
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, []);

  /**
   * 오더 정보 state 저장
   * --
   */
  React.useEffect(() => {
    if (ShopOrderList.length) {
      // 상품별 할인금, 적립금 정제
      const newShopOrderProduct = ShopOrderList.map((order) => ({
        ...order,
        product_information: order.product_information.map((product) => {
          const {
            apply_policy_info,
            tax_rate,
            tax_type,
            cart_num,
            final_reserve_price,
            origin_total_price, // 원가 + 옵션금
            coupon_info,
            commission,
          } = product;

          let gradePolicy = null;
          let promotionPolicy = null;
          let couponPolicy = null;

          // apply_policy_info 순회하며 필요한 값 추출
          const totalDiscount = apply_policy_info
            ? apply_policy_info?.reduce((acc, policy) => {
                acc += getPolicyDiscountPrice(policy, cart_num);

                if (policy.policy_type === 'COUPON') {
                  couponPolicy = policy;
                } else if (policy.policy_type === 'GRADE') {
                  gradePolicy = policy;
                } else {
                  promotionPolicy = policy;
                }

                return acc;
              }, 0)
            : 0;

          // 상품 최종 결제 금액
          let finalPrice = origin_total_price * cart_num - totalDiscount;

          // 렌탈일 경우 최종 결제금액을 월 결제 금액으로 전환
          finalPrice =
            deliveryType === 'rental'
              ? calculateRentalMonthlyPayment(
                  finalPrice,
                  product?.rental_option,
                )
              : finalPrice;

          const taxAmount = calculateTax(finalPrice, tax_rate, tax_type);

          const applyCommission = coupon_info
            ? extractApplicationCommission([
                commission,
                {
                  type: 'COUPON',
                  commission_rate: coupon_info.commission_rate,
                  commission_type: coupon_info.commission_type,
                },
              ])
            : commission;

          return {
            ...product,

            total_discount_price: totalDiscount,
            total_reserve_price: final_reserve_price ?? 0,

            product_final_price:
              deliveryType === 'rental'
                ? finalPrice
                : origin_total_price -
                  (gradePolicy?.discount_price ?? 0) -
                  (promotionPolicy?.discount_price ?? 0),

            ...taxAmount,

            grade_discount_price: getPolicyDiscountPrice(gradePolicy, cart_num), // 등급 할인 금액
            promotion_discount_price: getPolicyDiscountPrice(
              promotionPolicy,
              cart_num,
            ), // 프로모션 할인 금액
            coupon_discount_price: getPolicyDiscountPrice(
              couponPolicy,
              cart_num,
            ), // 쿠폰 할인 금액

            apply_commission: applyCommission, // 적용 수수료
          };
        }),
      }));

      // 출고지별 배송비, 총 할인금
      const newShopOrder = newShopOrderProduct.map((order) => {
        const {
          origin_free_delivery_price,
          origin_delivery_price,
          total_origin_price,
        } = order;
        const checkStorePickUp =
          order?.product_information[order.product_information.length - 1]
            ?.cart_type !== 'STORE_PICKUP';
        // 상품 총 할인금, 적립금 계산
        const finalTotal = order.product_information.reduce(
          (acc, cur) => {
            acc['final_total_discount_price'] += cur.total_discount_price;
            acc['final_total_reserve_price'] += cur.total_reserve_price;
            acc['final_grade_discount_price'] += cur.grade_discount_price;
            acc['final_coupon_discount_price'] += cur.coupon_discount_price;
            acc['final_promotion_discount_price'] +=
              cur.promotion_discount_price;
            acc['final_supply_price'] += cur.supply_price;
            acc['final_tax_amount'] += cur.tax_amount;
            acc['final_tax_free_amount'] += cur.tax_free_amount;

            return acc;
          },
          {
            final_total_discount_price: 0,
            final_total_reserve_price: 0,
            final_grade_discount_price: 0,
            final_coupon_discount_price: 0,
            final_promotion_discount_price: 0,
            final_supply_price: 0,
            final_tax_amount: 0,
            final_tax_free_amount: 0,
          },
        );

        return {
          ...order,
          ...finalTotal,
          delivery_price: checkStorePickUp
            ? total_origin_price - finalTotal.final_total_discount_price <
              origin_free_delivery_price
              ? origin_delivery_price
              : 0
            : 0,
        };
      });

      setShopOrder(newShopOrder);
    }
  }, [ShopOrderList, deliveryType]);

  /**
   * 결제 정보 (합산 금액 계산)
   * --
   */
  React.useEffect(() => {
    if (shopOrder?.length) {
      // 상품별 할인금, 적립금 정제
      const newShopOrderProduct = shopOrder.map((order) => ({
        ...order,
        product_information: order.product_information.map((product) => {
          const {
            price,
            option_price,
            cart_num,

            apply_policy_info,
            total_reserve_price,
          } = product;

          let gradePolicy = null;
          let couponPolicy = null;
          let promotionPolicy = null;

          // apply_policy 순회하며 합산금액 추출
          const totalDiscount = apply_policy_info
            ? apply_policy_info?.reduce((acc, policy) => {
                acc += getPolicyDiscountPrice(policy, cart_num);

                if (policy.policy_type === 'COUPON') {
                  couponPolicy = policy;
                } else if (policy.policy_type === 'GRADE') {
                  gradePolicy = policy;
                } else {
                  promotionPolicy = policy;
                }

                return acc;
              }, 0)
            : 0;

          // 상품 금액 * 수량 - 할인금
          // 상품 최종 결제 금액
          let finalPrice = (price + option_price) * cart_num - totalDiscount;

          // 렌탈일 경우 최종 결제금액을 월 결제 금액으로 전환
          finalPrice =
            deliveryType === 'rental'
              ? calculateRentalMonthlyPayment(
                  finalPrice,
                  product?.rental_option,
                )
              : finalPrice;

          return {
            ...product,
            total_discount_price: totalDiscount,
            total_reserve_price:
              usePoint && usePoint > 0 ? 0 : total_reserve_price,
            grade_discount_price: getPolicyDiscountPrice(gradePolicy, cart_num), // 등급 할인 금액
            coupon_discount_price: getPolicyDiscountPrice(
              couponPolicy,
              cart_num,
            ), // 쿠폰 할인 금액
            promotion_discount_price: getPolicyDiscountPrice(
              promotionPolicy,
              cart_num,
            ), // 프로모션 할인 금액
            product_final_price: finalPrice,
          };
        }),
      }));

      // 상품만 추출
      const productList = newShopOrderProduct.reduce((acc, cur) => {
        const { product_information } = cur;
        acc = acc.concat(product_information);

        return acc;
      }, []);

      // 상품만 추출하여 비율별 포인트 분배하여 새로운 상품리스트 반환
      const newProductList = handleDistributePointByRatio(
        productList,
        usePoint,
      );
      // 포인트 비율별 분배가 적용된 상품목록 정제하여 공급가액, 세액, 면세금액 게산
      const newProductInformation = newShopOrderProduct.map((order) => ({
        ...order,
        product_information: newProductList
          .filter(
            (product) =>
              order.forwarding_location_id === product.forwarding_location_id,
          )
          .map((product) => {
            const { product_final_price, apply_point, tax_rate, tax_type } =
              product;

            const taxAmount = calculateTax(
              product_final_price - apply_point,
              tax_rate,
              tax_type,
            );

            return {
              ...product,
              ...taxAmount,
            };
          }),
      }));

      // 출고지별 배송비, 총 할인금
      const newShopOrder = newProductInformation.map((order) => {
        const {
          origin_free_delivery_price,
          origin_delivery_price,
          total_origin_price,
        } = order;
        const checkStorePickUp =
          order.product_information[order.product_information.length - 1]
            .cart_type !== 'STORE_PICKUP';
        // 상품 총 할인금, 적립금 계산
        const finalTotal = order.product_information.reduce(
          (acc, cur) => {
            acc['final_total_discount_price'] += cur.total_discount_price;
            acc['final_total_reserve_price'] += cur.total_reserve_price;
            acc['final_grade_discount_price'] += cur.grade_discount_price;
            acc['final_coupon_discount_price'] += cur.coupon_discount_price;
            acc['final_promotion_discount_price'] +=
              cur.promotion_discount_price;
            acc['final_supply_price'] += cur.supply_price;
            acc['final_tax_amount'] += cur.tax_amount;
            acc['final_tax_free_amount'] += cur.tax_free_amount;

            if (cur.type === 'RENTAL') {
              acc['final_advance_price'] += cur.advance_price;
              acc['final_contract_date'] += cur.contract_date;
              acc['final_additional_cost'] += cur.additional_cost;
            }

            return acc;
          },
          {
            final_total_discount_price: 0,
            final_total_reserve_price: 0,
            final_grade_discount_price: 0,
            final_coupon_discount_price: 0,
            final_promotion_discount_price: 0,
            final_supply_price: 0,
            final_tax_amount: 0,
            final_tax_free_amount: 0,

            final_advance_price: 0,
            final_contract_date: 0,
            final_additional_cost: 0,
          },
        );

        return {
          ...order,
          ...finalTotal,
          delivery_price: checkStorePickUp
            ? total_origin_price - finalTotal.final_total_discount_price <
              origin_free_delivery_price
              ? origin_delivery_price
              : 0
            : 0,
        };
      });

      let total_product_count = -1,
        total_origin_delivery_price = 0,
        total_delivery_price = 0,
        total_remote_delivery_price = 0,
        total_discount_price = 0,
        total_reserve_price = 0,
        total_origin_price = 0,
        total_product_price = 0,
        total_final_price = 0,
        remote_delivery_area = '',
        product_name = '',
        total_coupon_info = [],
        shop_information_id = null,
        total_grade_discount_price = 0,
        total_coupon_discount_price = 0,
        total_promotion_discount_price = 0,
        total_supply_price = 0,
        total_tax_amount = 0,
        total_tax_free_amount = 0,
        advance_price = 0,
        contract_date = 0,
        additional_cost = 0;

      newShopOrder.forEach((order) => {
        // 총 상품 수량
        total_product_count += order.product_information.reduce(
          (acc) => ++acc,
          0,
        );
        shop_information_id = order.product_information[0].shop_information_id; // 대표 업체
        product_name = order.product_information[0].product_name; // 대표 상품명

        total_origin_delivery_price += order.origin_delivery_price; // 총 배송 원금
        total_delivery_price += order.delivery_price; // 총 배송비
        total_remote_delivery_price += order.remote_delivery_price; // 총 도서산간지역 배송비
        remote_delivery_area = order.remote_delivery_area; // 배송산간지역명

        total_product_price +=
          order.total_origin_price - order.final_total_discount_price; // 총 할인된 상품금액
        total_origin_price += order.total_origin_price; // 총 상품 원금

        total_discount_price += order.final_total_discount_price; // 총 할인금 (상품 할인 + 쿠폰 할인)
        total_reserve_price += order.final_total_reserve_price; // 총 적립금 (상품 적립 + 쿠폰 적립)

        total_final_price +=
          order.total_origin_price -
          order.final_total_discount_price +
          order.delivery_price +
          order.remote_delivery_price; // 최종 결제 금액 (총 상품 원금 - 총 할인금 + 배송비 + 도서산간지역 배송비)

        // 총 쿠폰 정보
        total_coupon_info = total_coupon_info.concat(
          order.product_information.reduce((acc, cur) => {
            if (cur.coupon_info) {
              acc.push(cur.coupon_info);
            }
            return acc;
          }, []),
        );

        total_grade_discount_price += order.final_grade_discount_price; // 총 등급 할인금

        total_coupon_discount_price += order.final_coupon_discount_price; // 총 쿠폰 할인금

        total_promotion_discount_price += order.final_promotion_discount_price; // 총 할인금

        total_supply_price += order.final_supply_price; // 총 공급가액
        total_tax_amount += order.final_tax_amount; // 총 세액
        total_tax_free_amount += order.final_tax_free_amount; // 총 면세금액

        advance_price += order.final_advance_price;
        contract_date += order.final_contract_date;
        additional_cost += order.final_additional_cost;
      });

      // 상태 업데이트
      setPaymentInfo({
        shop_information_id,
        total_product_count,
        total_product_price,
        total_origin_delivery_price,
        total_delivery_price,
        total_remote_delivery_price,
        total_discount_price,
        total_reserve_price: usePoint && usePoint > 0 ? 0 : total_reserve_price,
        total_origin_price,

        total_final_price,
        remote_delivery_area,
        total_coupon_info,
        product_name,

        total_grade_discount_price,
        total_coupon_discount_price,
        total_promotion_discount_price,

        total_supply_price,
        total_tax_amount,
        total_tax_free_amount,

        advance_price,
        contract_date,
        additional_cost,
      });
    }
  }, [shopOrder, usePoint, deliveryType]);

  /**
   * 팝업 메세지 감지
   * --
   */
  React.useEffect(() => {
    // 메시지 수신 핸들러
    const handleMessage = (event) => {
      const allowedOrigins = [
        `http://${DOMAIN_URL}${DEPLOY_TYPE === 'local' ? ':3000' : ''}`,
        `https://${DOMAIN_URL}${DEPLOY_TYPE === 'local' ? ':3000' : ''}`,
        `http://mall.${DOMAIN_URL}${DEPLOY_TYPE === 'local' ? ':3000' : ''}`,
        `https://mall.${DOMAIN_URL}${DEPLOY_TYPE === 'local' ? ':3000' : ''}`,
        `https://jm-coffee.${DOMAIN_URL}${
          DEPLOY_TYPE === 'local' ? ':3000' : ''
        }`,
        `https://mall-jm-coffee.${DOMAIN_URL}${
          DEPLOY_TYPE === 'local' ? ':3000' : ''
        }`,
      ];

      // 보안 체크: 같은 origin에서 온 메시지만 허용
      if (!allowedOrigins.includes(event.origin)) {
        console.warn('허용되지 않은 origin:', event.origin);
        return;
      }

      const message = event.data;
      // 팝업에서 보낸 데이터만 처리
      // NOTE: 배송지 등록
      if (message?.source === 'popup-create') {
        userDataAction.addData('deliveryAddress', JSON.parse(message?.data));
      }
      // NOTE: 배송지 업데이트
      else if (message?.source === 'popup-update') {
        const parseData = JSON.parse(message?.data);

        const fid = deliveryAddress.findIndex(
          (item) => item.delivery_address_id === parseData?.delivery_address_id,
        );

        if (fid === -1) return;

        const newArr = [
          ...deliveryAddress.slice(0, fid),
          parseData,
          ...deliveryAddress.slice(fid + 1),
        ];

        userDataAction.setData('deliveryAddress', newArr);
      }
      // NOTE: 결제 성공
      else if (message?.source === 'payment-success') {
        const parseData = JSON.parse(message?.data);
        window.closeEvent();
        // if (parseData?.jm_pay || parseData?.jm_pay > 0) {
        //   console.log(parseData, '<< parseData');
        //   userAction.setUser({
        //     ...userInfo,
        //     jm_pay: userInfo.jm_pay + Number(parseData.jm_pay),
        //   });
        //   setPaymentReturn({
        //     status: 'success',
        //     data: parseData,
        //   });
        //   setIsProgress(false);
        //   return;
        // }
        setPaymentReturn({
          status: 'success',
          data: parseData,
        });
        setIsProgress(false);
      }
      // NOTE: 결제 실패
      else if (message?.source === 'payment-fail') {
        const parseData = JSON.parse(message?.data);
        window.closeEvent();

        setPaymentReturn({
          status: 'fail',
          data: parseData,
        });
        setIsProgress(false);
      }
      // NOTE: 카드 등록 성공
      else if (message?.source === 'card-register') {
        const parseData = JSON.parse(message?.data);

        userDataAction.addData('cardList', parseData);
      }
    };

    window.addEventListener('message', handleMessage);

    // 컴포넌트 언마운트 시 이벤트 리스너 제거
    return () => {
      window.removeEventListener('message', handleMessage);
    };
    // eslint-disable-next-line
  }, []);

  /**
   * 결제 리턴 후 오더 생성
   * --
   */
  React.useEffect(() => {
    if (!paymentReturn) return;
    const useJmPay = sessionStorage.getItem('useJmPay');
    if (
      paymentReturn.data?.trno
        ? tidId === paymentReturn.data?.trno && paymentReturn.data?.trno
        : tidId === paymentReturn.data?.tid && paymentReturn.data?.tid
    ) {
      setPaymentReturn(null);
      setIsLoading(false);
      return;
    }
    setTidId(
      paymentReturn.data?.trno
        ? paymentReturn.data?.trno
        : paymentReturn.data?.tid,
    );
    if (isOpenModal || useJmPay === 'YES') {
      handlePaymentSubmit('JM_PAY-SUCCESS', paymentReturn.data);
      sessionStorage.removeItem('useJmPay');
      return;
    } else {
      handlePaymentSubmit(paymentReturn.status, paymentReturn.data);
    }
    // eslint-disable-next-line
  }, [paymentReturn]);

  /**
   * 결제 팝업 열림상태 확인
   * --
   */
  React.useEffect(() => {
    let popupCheck;

    if (isProgress) {
      // 1초마다 팝업 상태 확인
      popupCheck = setInterval(() => {
        if (window._kspay_pop_obj?.closed) {
          window.closeIframe();
          setIsProgress(false);
          clearInterval(popupCheck); // 팝업이 닫히면 타이머 종료
        } else {
          return;
        }
      }, 1000);
    }

    return () => {
      if (popupCheck) {
        clearInterval(popupCheck);
      }
    };
  }, [isProgress]);

  /**
   * 앱 결제 후 오더 생성 처리
   * --
   */
  React.useEffect(() => {
    if (!payData) return;

    const { source, data } = JSON.parse(decodeURIComponent(payData));
    // const parseData = JSON.parse(decodeURIComponent(orData));

    const getItems = sessionStorage.getItem('orderData');
    const getDelivery = sessionStorage.getItem('deliveryAddress');
    const useJmPay = sessionStorage.getItem('useJmPay');
    if (useJmPay === 'YES') {
      setPaymentMethod('JM_PAY');
    }
    const parseData = JSON.parse(getItems);
    const parseDelivery = JSON.parse(getDelivery);

    setShopOrder(parseData);
    setSelectedDelivery(parseDelivery);
    setTimeout(() => {
      setPaymentReturn({
        status: source === 'payment-fail' ? 'fail' : 'success',
        data: JSON.parse(data),
      });
    }, 500);
  }, [payData]);

  /**
   * 최대 사용 가능 포인트
   * --
   */
  const maxUseablePoint = React.useMemo(() => {
    if (!settings) return 0;

    const { point_setting } = settings;

    return point_setting?.point_usage_limit_ratio;
  }, [settings]);

  /**
   * 환불불가 상품 포함 여부 체크
   * --
   */
  React.useEffect(() => {
    if (!shopOrderList.length) return;

    const isExist = shopOrderList.some((item) => item?.is_refund === 'N');

    setIsRefundNotAllowed(isExist);
  }, [shopOrderList]);

  /**
   * 제이엠페이 잔액 충전
   * --
   */
  React.useEffect(() => {
    if (isJmPayCharge <= 0) return;
    setIsOpenModal(true);
  }, [isJmPayCharge]);

  /* ===== RENDER ===== */
  return (
    <>
      <OrderOrderPresenter
        orderList={shopOrder}
        deliveryAddress={DeliveryAddress}
        selectedDelivery={selectedDelivery}
        paymentInfo={paymentInfo}
        setSelectedDelivery={setSelectedDelivery}
        onPayment={handlePayment}
        onOpenPopup={handleOpenPopup}
        user={userInfo}
        onDeleteAddress={handleDeleteAddress}
        isLoading={isLoading}
        setIsLoading={setIsLoading}
        setPaymentMethod={setPaymentMethod}
        coupons={MyCouponData}
        selectedCoupon={selectedCoupon}
        setSelectedCoupon={setSelectedCoupon}
        selectedProduct={selectedProduct}
        setSelectedProduct={setSelectedProduct}
        onApplyCoupon={handleApplyCoupon}
        type={deliveryType}
        cardList={MyCardList}
        onOpenCardPopup={handleOpenCardPopup}
        selectedCard={selectedCard}
        setSelectedCard={setSelectedCard}
        deliveryDate={deliveryDate}
        setDeliveryDate={setDeliveryDate}
        minDeliveryDate={MinDeliveryDate}
        point={MyPoint}
        usePoint={usePoint}
        setUsePoint={setUsePoint}
        isChecked={isChecked}
        setIsChecked={setIsChecked}
        isAgree={isAgree}
        setIsAgree={setIsAgree}
        screenSize={screenSize}
        paymentType={paymentType}
        maxUseablePoint={maxUseablePoint}
        isRefundNotAllowed={isRefundNotAllowed}
        isRefundAgree={isRefundAgree}
        setIsRefundAgree={setIsRefundAgree}
        onDeleteCard={handleDeleteCard}
        today={today}
      />
      {isOpenModal && (
        <JmPayCharge
          isOpenModal={isOpenModal}
          setIsOpenModal={setIsOpenModal}
          payment={isJmPayCharge}
          setPayment={setIsJmPayCharge}
          screenSize={screenSize}
          handlePaymentSubmit={handlePaymentSubmit}
          setPaymentReturn={setPaymentReturn}
          setIsLoading={setIsLoading}
          isLoading={isLoading}
          setIsProgress={setIsProgress}
          location={location}
          shopOrder={shopOrder}
          selectedDelivery={selectedDelivery}
        />
      )}
    </>
  );
};

export default OrderOrderContainer;
